import dayjs, { Dayjs } from "dayjs";
import { formatNumber, numberWithCommas } from "../../utils";
import {
  VisitBucketKey,
  VisitFrequencyCounts,
  VisitFrequencyMode,
  VisitFrequencyRoute,
  VisitThresholds,
} from "../../types/visit-frequency.type";

export const API_DATE = "YYYY-MM-DD";

/** The server rejects wider ranges; the page blocks them before asking. */
export const MAX_RANGE_DAYS = 92;

/** Below this many working days the server answers in `visited_only` mode. */
export const MIN_FREQUENCY_WORKING_DAYS = 6;

export const BUCKET_LABEL: Record<VisitBucketKey, string> = {
  daily: "ทุกวัน",
  alternate: "วันเว้นวัน",
  twice_weekly: "อาทิตย์ละ 2 ครั้ง",
  weekly: "อาทิตย์ละครั้ง",
  visited: "เข้าร้าน",
  none: "ไม่เข้าร้าน",
};

/** Most-visited first; `none` is always last. */
export const BUCKETS_BY_MODE: Record<VisitFrequencyMode, VisitBucketKey[]> = {
  frequency: ["daily", "alternate", "twice_weekly", "weekly", "none"],
  visited_only: ["visited", "none"],
};

export const bucketLabel = (key: string | null | undefined): string =>
  key && key in BUCKET_LABEL ? BUCKET_LABEL[key as VisitBucketKey] : "-";

export const isBucketKey = (value: string | null | undefined): value is VisitBucketKey =>
  Boolean(value && value in BUCKET_LABEL);

/** A bucket's share of the shop universe, or `null` when there are no shops to divide by. */
export const percentOf = (count: number | null | undefined, shops: number): number | null => {
  if (count === null || count === undefined || !shops) return null;
  return (count / shops) * 100;
};

/** "12.3%" to one decimal; "-" when there is no denominator. */
export const formatPercent = (count: number | null | undefined, shops: number): string => {
  const pct = percentOf(count, shops);
  return pct === null ? "-" : `${formatNumber(pct, { decimals: 1 })}%`;
};

export const formatCount = (value: number | null | undefined): string => numberWithCommas(value ?? 0);

/**
 * Visit-day range each bucket covers, read off the server's lower bounds: a bucket runs from
 * its own threshold up to one below the next-busier bucket's, and the busiest runs up to N.
 * `none` is always 0. Returns `null` for a bucket the thresholds do not describe.
 */
export const thresholdRange = (
  key: VisitBucketKey,
  mode: VisitFrequencyMode,
  thresholds: VisitThresholds,
  workingDays: number,
): { min: number; max: number | null } | null => {
  if (key === "none") return { min: 0, max: 0 };
  const order = BUCKETS_BY_MODE[mode].filter((b) => b !== "none");
  const index = order.indexOf(key);
  const min = thresholds[key];
  if (index < 0 || min === undefined || min === null) return null;
  if (index === 0) {
    // The busiest bucket is open-ended in visit days (a Sunday visit can push d past N),
    // but N is the number the reader is comparing against.
    return { min, max: mode === "visited_only" ? null : Math.max(min, workingDays) };
  }
  const busier = thresholds[order[index - 1]];
  return { min, max: busier === undefined || busier === null ? null : Math.max(min, busier - 1) };
};

/** "24–25 วัน", "1 วันขึ้นไป", "0 วัน". Empty when the thresholds do not cover the bucket. */
export const thresholdText = (
  key: VisitBucketKey,
  mode: VisitFrequencyMode,
  thresholds: VisitThresholds,
  workingDays: number,
): string => {
  const range = thresholdRange(key, mode, thresholds, workingDays);
  if (!range) return "";
  if (range.max === null) return `${range.min} วันขึ้นไป`;
  if (range.min === range.max) return `${range.min} วัน`;
  return `${range.min}–${range.max} วัน`;
};

/** "ทุกวัน (24–25 วัน)" — a column header carrying its own definition. */
export const bucketHeader = (
  key: VisitBucketKey,
  mode: VisitFrequencyMode,
  thresholds: VisitThresholds,
  workingDays: number,
): string => {
  const range = thresholdText(key, mode, thresholds, workingDays);
  return range ? `${BUCKET_LABEL[key]} (${range})` : BUCKET_LABEL[key];
};

/** "KB01 ปราจีน-นครนายก"; "ไม่มีสาย" for the route-less bucket. */
export const routeLabel = (
  route: Pick<VisitFrequencyRoute, "route_number" | "route_code" | "route_name">,
): string => {
  if (route.route_number === null || route.route_number === "") return "ไม่มีสาย";
  const code = route.route_code ?? route.route_number;
  return route.route_name ? `${code} ${route.route_name}` : code;
};

// ── Range & holidays ──────────────────────────────────────────────────────────────────────

export const spanDays = (from: Dayjs, to: Dayjs): number => to.diff(from, "day") + 1;

export const isRangeTooLong = (from: Dayjs, to: Dayjs): boolean =>
  spanDays(from, to) > MAX_RANGE_DAYS;

export const isSunday = (date: string | Dayjs): boolean => dayjs(date).day() === 0;

/**
 * The holidays worth sending: inside the range, not a Sunday (always off already), unique and
 * sorted — so a range change never leaves a stale date in the request.
 */
export const normalizeHolidays = (holidays: string[], from: Dayjs, to: Dayjs): string[] => {
  const lo = from.format(API_DATE);
  const hi = to.format(API_DATE);
  return Array.from(new Set(holidays))
    .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d) && d >= lo && d <= hi && !isSunday(d))
    .sort();
};

/** Parses the `holidays` query value (comma-separated) back into a list. */
export const parseHolidayParam = (value: string | null): string[] =>
  value ? value.split(",").map((d) => d.trim()).filter(Boolean) : [];

// ── CSV export ───────────────────────────────────────────────────────────────────────────

/**
 * A cell that starts with = + - @ tab or CR is read by Excel as a formula; shop names come
 * from field data, so they are made plain text first (same guard as the แผนวิ่ง report).
 */
const neutralizeFormula = (value: string): string =>
  /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;

export const escapeCsvCell = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") return String(value);
  return `"${neutralizeFormula(value).replace(/"/g, '""')}"`;
};

export const buildCsv = (rows: (string | number | null | undefined)[][]): string =>
  rows.map((row) => row.map(escapeCsvCell).join(",")).join("\r\n");

/** Percent as a plain number rounded to 1 dp, so Excel can still sum/sort the column. */
const csvPercent = (count: number | undefined, shops: number): number | string => {
  const pct = percentOf(count, shops);
  return pct === null ? "" : Math.round(pct * 10) / 10;
};

export interface CountsCsvRow {
  cells: (string | number | null | undefined)[];
  counts: VisitFrequencyCounts;
}

/**
 * The รวม-sheet layout: leading identity columns, then shops, each bucket as count + %, then
 * เข้าแต่ขายไม่ได้ and พักใช้งาน.
 */
export const buildCountsCsv = (options: {
  leadHeaders: string[];
  rows: CountsCsvRow[];
  total?: CountsCsvRow;
  mode: VisitFrequencyMode;
  thresholds: VisitThresholds;
  workingDays: number;
  preamble?: (string | number)[][];
}): string => {
  const buckets = BUCKETS_BY_MODE[options.mode];
  const header = [
    ...options.leadHeaders,
    "จำนวนร้านค้า",
    ...buckets.flatMap((b) => [
      bucketHeader(b, options.mode, options.thresholds, options.workingDays),
      `% ${BUCKET_LABEL[b]}`,
    ]),
    "เข้าแต่ขายไม่ได้",
    "พักใช้งาน",
  ];
  const line = ({ cells, counts }: CountsCsvRow) => [
    ...cells,
    counts.shops,
    ...buckets.flatMap((b) => [counts.buckets[b] ?? 0, csvPercent(counts.buckets[b] ?? 0, counts.shops)]),
    counts.visited_not_sold_shops,
    counts.suspended_shops,
  ];
  return buildCsv([
    ...(options.preamble ?? []),
    ...(options.preamble?.length ? [[]] : []),
    header,
    ...options.rows.map(line),
    ...(options.total ? [line(options.total)] : []),
  ]);
};

/** Excel on Windows needs the BOM to read Thai as UTF-8. */
export const csvBlob = (csv: string): Blob =>
  new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8;" });
